import { getAccount, getUser } from "@hardware/Store/Context/ContextSelectors";
import customGetProductCategories from "@hardware/Store/Handlers/Utilities/CustomGetProductCategories";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { HasProduct, withProduct } from "@insite/client-framework/Components/ProductContext";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getSession } from "@insite/client-framework/Store/Context/ContextSelectors";
import translate from "@insite/client-framework/Translate";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import { AccordionSectionPresentationProps, ManagedAccordionSection } from "@insite/mobius/AccordionSection";
import GridContainer, { GridContainerProps } from "@insite/mobius/GridContainer";
import GridItem, { GridItemProps } from "@insite/mobius/GridItem";
import { IconProps } from "@insite/mobius/Icon";
import Link, { LinkPresentationProps } from "@insite/mobius/Link";
import { TypographyPresentationProps, TypographyProps } from "@insite/mobius/Typography";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import * as React from "react";
import { useEffect, useState } from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

const mapStateToProps = (state: ApplicationState) => {
    const { isAuthenticated } = getSession(state);
    return {
        showAccountNumber: isAuthenticated,
        account: getAccount(state),
        user: getUser(state),
        billtoId: state.context.session.billToId,
    };
};

const mapDispatchToProps = {
    customGetProductCategories,
};

type OwnProps = ReturnType<typeof mapStateToProps> &
    WidgetProps &
    HasProduct &
    ResolveThunks<typeof mapDispatchToProps>;

export interface ProductDetailsDrawingsStyles {
    wrapper?: InjectableCss;
    container?: GridContainerProps;
    documentGridItem?: GridItemProps;
    documentLink?: LinkPresentationProps;
    documentIcon?: IconProps;
    documentNameText?: TypographyPresentationProps;
    titleText?: TypographyProps;
    typographyProps?: InjectableCss;
    specificationsAccordionSection?: AccordionSectionPresentationProps;
}

export const drawingsStyles: ProductDetailsDrawingsStyles = {
    wrapper: {
        css: css`
            padding: 10px 15px 30px;
        `,
    },
    documentGridItem: {
        width: [12, 12, 6, 6, 6],
    },
    documentNameText: {
        css: css`
            vertical-align: super;
        `,
    },
    titleText: {
        variant: "h3",
        css: css`
            @media print {
                font-size: 12px;
            }
            margin-bottom: 5px;
            font-size: 16px;
            padding-top: 10px;
        `,
    },
    typographyProps: {
        css: css`
            text-align: left;
            font-size: 15px;
            font-weight: 700;
            color: #094e66;
            text-decoration: none;
        `,
    },
};

const styles = drawingsStyles;

const ProductDetailsDrawings: React.FC<OwnProps> = ({
    product: { id, productNumber },
    customGetProductCategories,
    account,
    billtoId,
    user,
}) => {
    const [showDrawingsProduct, setShowDrawingsProduct] = useState(false);

    if (!id || !account) {
        return null;
    }

    useEffect(() => {
        customGetProductCategories({
            productIds: [id],
            type: "product",
            onSuccess: data => {
                const properties = data.productProperties[0]?.customProperties;
                if (properties) {
                    const erpDrawing = properties["erpDrawing"];
                    if (erpDrawing) {
                        setShowDrawingsProduct(erpDrawing === "True");
                    }
                }
            },
        });
    }, [id]);

    const drawingUrl = `/api/v1/productpaperwork/GetPdf?productNumber=${productNumber}&userEmail=${user.email}`;

    return (
        <ManagedAccordionSection
            title={translate("Drawings")}
            data-test-selector="productDetails_drawings"
            initialExpanded={false}
            {...styles.specificationsAccordionSection}
        >
            <StyledWrapper {...styles.wrapper}>
                <GridContainer {...styles.container}>
                    <GridItem {...styles.documentGridItem}>
                        <Link href={drawingUrl} target="_new" {...styles.typographyProps}>
                            {translate("Click here to download related Drawings")}
                        </Link>
                    </GridItem>
                </GridContainer>
            </StyledWrapper>
        </ManagedAccordionSection>
    );
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(withProduct(ProductDetailsDrawings)),
    definition: {
        displayName: "Drawings",
        group: "Product Details",
        allowedContexts: ["ProductDetailsPage"],
    },
};

export default widgetModule;
